import { Box2, Box3, Frustum, Plane, Ray, Vector2, Vector3 } from "three";


export namespace LineSegmentUtils {
  //https://www.geeksforgeeks.org/check-if-two-given-line-segments-intersect/
  // Given three collinear points p, q, r, the function checks if
  // point q lies on line segment 'pr'
  function onSegment(p: Vector2, q: Vector2, r: Vector2) {
    if (
      q.x <= Math.max(p.x, r.x) &&
      q.x >= Math.min(p.x, r.x) &&
      q.y <= Math.max(p.y, r.y) &&
      q.y >= Math.min(p.y, r.y)
    )
      return true;

    return false;
  }

  // To find orientation of ordered triplet (p, q, r).
  // The function returns following values
  // 0 --> p, q and r are collinear
  // 1 --> Clockwise
  // 2 --> Counterclockwise
  function orientation(p: Vector2, q: Vector2, r: Vector2) {
    // See https://www.geeksforgeeks.org/orientation-3-ordered-points/
    // for details of below formula.
    let val = (q.y - p.y) * (r.x - q.x) - (q.x - p.x) * (r.y - q.y);

    if (val === 0) return 0; // collinear

    return val > 0 ? 1 : 2; // clock or counterclock wise
  }

  // The main function that returns true if line segment 'p1q1'
  // and 'p2q2' intersect.
  export function doIntersect(
    p1: Vector2,
    q1: Vector2,
    p2: Vector2,
    q2: Vector2
  ) {
    // Find the four orientations needed for general and
    // special cases
    let o1 = orientation(p1, q1, p2);
    let o2 = orientation(p1, q1, q2);
    let o3 = orientation(p2, q2, p1);
    let o4 = orientation(p2, q2, q1);

    // General case
    if (o1 !== o2 && o3 !== o4) return true;

    // Special Cases
    // p1, q1 and p2 are collinear and p2 lies on segment p1q1
    if (o1 === 0 && onSegment(p1, p2, q1)) return true;

    // p1, q1 and q2 are collinear and q2 lies on segment p1q1
    if (o2 === 0 && onSegment(p1, q2, q1)) return true;

    // p2, q2 and p1 are collinear and p1 lies on segment p2q2
    if (o3 === 0 && onSegment(p2, p1, q2)) return true;

    // p2, q2 and q1 are collinear and q1 lies on segment p2q2
    if (o4 === 0 && onSegment(p2, q1, q2)) return true;

    return false; // Doesn't fall in any of the above cases
  }
}

export namespace PolygonUtils {
  export function isPointInsidePolygon2D(
    x: number,
    y: number,
    polygon: Vector2[] | Vector3[],
    polygonbbox: Box2
  ): boolean {
    //if the line from x,y passes polygon, and if it crosses even times it is outside, otherwise it is inside;
    // line x,y-> endX, y
    const endX = polygonbbox.max.x;
    const p1 = new Vector2(x, y);
    const q1 = new Vector2(endX, y);

    let intersectsCount = 0;

    for (let i = 0; i < polygon.length; ++i) {
      const ni = (i + 1) % polygon.length;
      const p2 = polygon[i] as Vector2;
      const q2 = polygon[ni] as Vector2;
      if (LineSegmentUtils.doIntersect(p1, q1, p2, q2)) {
        intersectsCount++;
      }
    }
    return intersectsCount % 2 === 0 ? false : true;
  }

  export function isBoxInsidePolygon2D(
    box: Box2,
    polygon: Vector2[] | Vector3[],
    polygonbbox: Box2
  ): boolean {
    //check if any of the polygon points lies inside box
    for (const p of polygon) {
      if (box.containsPoint(p as Vector2)) {
        return true;
      }
    }

    const pointsFromBox = [
      box.min,
      new Vector2(box.min.x, box.max.y),
      box.max,
      new Vector2(box.max.x, box.min.y)
    ]

    //check if any of the box points lies inside polygon
    for (const p of pointsFromBox) {
      if (isPointInsidePolygon2D(p.x, p.y, polygon, polygonbbox)) {
        return true;
      }
    }
    return false;
  }

  export function intersectionTest(
    polygonMesh: { vertices: Vector3[]; indices: number[] },
    ray: Ray
  ): Vector3 | undefined {
    for (let i = 0; i < polygonMesh.indices.length; i += 3) {
      const hit = ray.intersectTriangle(
        polygonMesh.vertices[polygonMesh.indices[i]],
        polygonMesh.vertices[polygonMesh.indices[i + 1]],
        polygonMesh.vertices[polygonMesh.indices[i + 2]],
        false,
        new Vector3()
      );
      if (hit) {
        return hit;
      }
    }
  }
}

export namespace FrustumUtil {
  export enum TileIntersectsFrustum {
    "noIntersection",
    "justIntersects",
    "iscompletelyInside",
  }

  /**
   * 範囲内にあるタイル数だけ衝突判定
   * @param frustum - カメラ範囲
   * @param bbox - タイルの境界矩形（位置、サイズ、衝突判定などに使用）
   * @param threshold - 分割数制御のしきい値
   * @returns {Number} - 0（タイルなし）、1（一部タイル画面内）、2（全タイル画面内）
   */
  export function intersectsBox(
    frustum: Frustum,
    bbox: Box3,
    threshold: number
  ): TileIntersectsFrustum {
    const planes = frustum.planes;
    let result = TileIntersectsFrustum.iscompletelyInside;
    const vector = new Vector3();

    // check if all all boxes are inside the camera range
    for (let i = 0; i < planes.length; i++) {
      const plane = planes[i]; // corner at max distance

      vector.x = plane.normal.x > 0 ? bbox.max.x : bbox.min.x;
      vector.y = plane.normal.y > 0 ? bbox.max.y : bbox.min.y;
      vector.z = plane.normal.z > 0 ? bbox.max.z : bbox.min.z;

      const distance = plane.distanceToPoint(vector);
      // if a box is outside of the box, marked as 'noIntersection'
      if (distance < 0) {
        result = TileIntersectsFrustum.noIntersection;
        break;
      }
    }

    if (result) {
      for (let i = 0; i < planes.length; i++) {
        const plane = planes[i].clone(); // corner at max distance

        vector.x = plane.normal.x < 0 ? bbox.max.x : bbox.min.x;
        vector.y = plane.normal.y < 0 ? bbox.max.y : bbox.min.y;
        vector.z = plane.normal.z < 0 ? bbox.max.z : bbox.min.z;
        plane.constant = plane.constant / threshold;

        const distance = plane.distanceToPoint(vector);
        if (distance < 0) {
          result = TileIntersectsFrustum.justIntersects;
          break;
        }
      }
    }
    return result;
  }
}

/**
 * @param intersectionIsValid defaults to true. if true, intersecting boxes will count as being in front of the planes
 */
export const boxIsInFrontOfPlanes = (
  planes: Plane[],
  box: Box3,
  intersectionIsValid: boolean = true
): boolean => {
  const boxCenter = box.getCenter(new Vector3());
  if (intersectionIsValid) {
    for (const plane of planes) {
      if (
        plane.distanceToPoint(boxCenter) < 0 &&
        !plane.intersectsBox(box)
      ) {
        return false;
      }
    }
  } else {
    for (const plane of planes) {
      if (
        plane.distanceToPoint(boxCenter) < 0 ||
        plane.intersectsBox(box)
      ) {
        return false;
      }
    }
  }

  return true;
};

/**
 * returns output with all boxes from the boxes list that are in front of the planes.
 * @param intersectionIsValid defaults to true. if true, intersecting boxes will count as being in front of the planes
 */
export const boxesInsideFrustum = (
  planes: Plane[],
  boxes: Box3[],
  intersectionIsValid: boolean = true,
  output: Box3[]
): Box3[] => {
  for (const box of boxes) {
    if (boxIsInFrontOfPlanes(planes, box, intersectionIsValid)) {
      output.push(box);
    }
  }
  return output;
};

/**
 * exists to maintain consistent precision throughout core
 */
export const CLOSE_TO_ZERO = 0.0001;

/**
 * find minimum multiples of x
 * ex. value 130
 *    2, 4, 8, 16, 32, 64, 128, 256 <- hit
 * @param value
 * @param starts
 * @param multiplier
 * @returns
 */
export const findMultiplesOf2 = (value: number): number => {
  let current = 2;
  while (current < value) {
    current *= 2;
  }
  return current;
};